import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Icon from '../../components/AppIcon';
import './Bot.css';

const API_URL = 'https://api.autocasses.com/api/user/chatbot';

const Bot = () => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Messages d'accueil initiaux
  const initialMessages = [
    {
      id: '1',
      text: '👋 Bonjour ! Je suis votre assistant AutoCasses.',
      sender: 'bot',
      timestamp: new Date(),
      isWelcome: true
    },
    {
      id: '2',
      text: 'Je peux vous aider à trouver des pièces auto, répondre à vos questions ou vous guider dans vos achats.',
      sender: 'bot',
      timestamp: new Date(),
    },
  ];

  // Suggestions rapides catégorisées
  const quickSuggestions = [
    {
      category: 'Recherche',
      icon: 'Search',
      color: '#3b82f6',
      items: [
        { id: '1', text: 'Rechercher une pièce', icon: '🔍' },
        { id: '2', text: 'Pièces par véhicule', icon: '🚗' },
      ]
    },
    {
      category: 'Assistance',
      icon: 'HelpCircle',
      color: '#10b981',
      items: [
        { id: '3', text: 'Aide au diagnostic', icon: '🛠️' },
        { id: '4', text: 'Conseils mécaniques', icon: '⚙️' },
      ]
    },
    {
      category: 'Achat',
      icon: 'ShoppingCart',
      color: '#f59e0b',
      items: [
        { id: '5', text: 'Comparer des prix', icon: '💰' },
        { id: '6', text: 'Suivi commande', icon: '📦' },
      ]
    }
  ];

  useEffect(() => {
    setMessages(initialMessages);
  }, []);

  // Auto-scroll vers le bas quand de nouveaux messages arrivent
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping]);

  const sendMessageToAPI = async (message) => {
    try {
      setIsLoading(true);
      
      const response = await axios.post(API_URL, {
        message: message
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 15000,
      });

      setIsLoading(false);
      
      if (response.data && response.data.message) {
        return {
          success: true,
          message: response.data.message
        };
      } else {
        return {
          success: false,
          message: "Réponse invalide du serveur"
        };
      }

    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      setIsLoading(false);
      
      let errorMessage = 'Impossible de contacter le chatbot.';
      
      if (error.response) {
        console.error('Response error:', error.response.data);
        errorMessage = `Erreur serveur: ${error.response.status}`;
      } else if (error.request) {
        console.error('Request error:', error.request);
        errorMessage = 'Pas de réponse du serveur. Vérifiez votre connexion.';
      } else {
        console.error('Setup error:', error.message);
      }
      
      return {
        success: false,
        message: errorMessage
      };
    }
  };

  const handleSend = async () => {
    if (inputText.trim() === '' && selectedImages.length === 0) return;

    // Ajouter le message utilisateur
    const userMessage = {
      id: Date.now().toString(),
      text: inputText,
      sender: 'user',
      timestamp: new Date(),
      images: selectedImages.length > 0 ? [...selectedImages] : null,
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setSelectedImages([]);
    setShowSuggestions(false);

    // Simuler une réponse du bot
    setIsTyping(true);

    try {
      const apiResponse = await sendMessageToAPI(inputText);

      setTimeout(() => {
        if (apiResponse.success) {
          const botMessage = {
            id: (Date.now() + 1).toString(),
            text: apiResponse.message,
            sender: 'bot',
            timestamp: new Date(),
          };
          
          setMessages(prev => [...prev, botMessage]);
        } else {
          const fallbackResponses = [
            "Je comprends votre question concernant les pièces auto. Pouvez-vous préciser le modèle et l'année de votre véhicule ?",
            "Pour cette pièce spécifique, je vous recommande de vérifier notre catalogue dans la section 'Recherche avancée'.",
            "Merci pour les photos ! Je peux voir qu'il s'agit d'un problème de freins. Avez-vous essayé de vérifier les plaquettes ?",
            "Je vous conseille de consulter un mécanicien certifié pour ce type de diagnostic complexe.",
            "Je peux vous orienter vers les meilleurs vendeurs pour cette pièce. Souhaitez-vous que je vous montre des options ?",
            "Pour votre sécurité, je recommande toujours d'utiliser des pièces d'origine ou certifiées équivalentes.",
          ];

          const randomResponse = fallbackResponses[Math.floor(Math.random() * fallbackResponses.length)];
          
          const fallbackMessage = {
            id: (Date.now() + 1).toString(),
            text: randomResponse,
            sender: 'bot',
            timestamp: new Date(),
          };

          setMessages(prev => [...prev, fallbackMessage]);
        }
        setIsTyping(false);
      }, 1500);

    } catch (error) {
      console.error('Erreur dans handleSend:', error);
      
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        text: "Désolé, une erreur est survenue. Veuillez réessayer.",
        sender: 'bot',
        timestamp: new Date(),
      };
      
      setMessages(prev => [...prev, errorMessage]);
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    
    const validFiles = files.filter(file => {
      // Vérifier la taille (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert(`Le fichier ${file.name} dépasse la taille maximale de 10MB`);
        return false;
      }
      
      // Vérifier le type (images seulement)
      if (!file.type.startsWith('image/')) {
        alert(`Le fichier ${file.name} n'est pas une image valide`);
        return false;
      }
      
      return true;
    });

    if (selectedImages.length + validFiles.length > 3) {
      alert('Vous pouvez sélectionner maximum 3 images');
      return;
    }

    const newImages = validFiles.map(file => ({
      file: file,
      url: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
    }));

    setSelectedImages(prev => [...prev, ...newImages]);
    
    // Reset l'input file
    e.target.value = '';
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const handleRemoveImage = (index) => {
    // Libérer l'URL de l'image
    URL.revokeObjectURL(selectedImages[index].url);
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleClearImages = () => {
    // Libérer toutes les URLs
    selectedImages.forEach(image => URL.revokeObjectURL(image.url));
    setSelectedImages([]);
  };

  const handleQuickSuggestion = async (suggestion) => {
    const userMessage = {
      id: Date.now().toString(),
      text: suggestion.text,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setShowSuggestions(false);

    setIsTyping(true);

    try {
      const apiResponse = await sendMessageToAPI(suggestion.text);

      setTimeout(() => {
        if (apiResponse.success) {
          const botMessage = {
            id: (Date.now() + 1).toString(),
            text: apiResponse.message,
            sender: 'bot',
            timestamp: new Date(),
          };
          setMessages(prev => [...prev, botMessage]);
        } else {
          let botResponse = "";
          
          switch(suggestion.id) {
            case '1':
              botResponse = "Je vais vous aider à trouver la pièce dont vous avez besoin. Quel est le modèle exact de votre véhicule ?";
              break;
            case '2':
              botResponse = "Parfait ! Pour vous aider à trouver des pièces, j'ai besoin de connaître la marque, le modèle et l'année de votre véhicule.";
              break;
            case '3':
              botResponse = "Pour vous aider avec le diagnostic, pouvez-vous décrire les symptômes que vous observez ?";
              break;
            case '4':
              botResponse = "Je serai ravi de vous donner des conseils mécaniques. Sur quel aspect avez-vous besoin d'aide ?";
              break;
            case '5':
              botResponse = "Je peux vous aider à comparer les prix. De quelle pièce avez-vous besoin exactement ?";
              break;
            case '6':
              botResponse = "Pour le suivi de commande, j'ai besoin de votre numéro de commande. L'avez-vous sous la main ?";
              break;
            default:
              botResponse = "Merci pour votre message. Comment puis-je vous aider ?";
          }

          const fallbackMessage = {
            id: (Date.now() + 1).toString(),
            text: botResponse,
            sender: 'bot',
            timestamp: new Date(),
          };

          setMessages(prev => [...prev, fallbackMessage]);
        }
        setIsTyping(false);
      }, 1500);

    } catch (error) {
      console.error('Erreur dans handleQuickSuggestion:', error);
      
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        text: "Désolé, une erreur est survenue. Veuillez réessayer.",
        sender: 'bot',
        timestamp: new Date(),
      };
      
      setMessages(prev => [...prev, errorMessage]);
      setIsTyping(false);
    }
  };

  const handleClearChat = () => {
    if (window.confirm('Êtes-vous sûr de vouloir effacer tout l\'historique de chat ?')) {
      setMessages(initialMessages);
      setSelectedImages([]);
      setInputText('');
      setShowSuggestions(true);
    }
  };

  const formatTime = (date) => {
    const now = new Date();
    const messageDate = new Date(date);
    const diffMs = now - messageDate;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    
    return messageDate.toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const renderMessage = (item, index) => {
    const isUser = item.sender === 'user';
    const isFirstInGroup = index === 0 || messages[index - 1].sender !== item.sender;
    
    return (
      <div
        key={item.id}
        className={`message-container ${isUser ? 'user-message' : 'bot-message'}`}
      >
        {!isUser && isFirstInGroup && (
          <div className="bot-avatar">
            <div className="bot-avatar-icon">
              <Icon name="Car" size={20} color="#fff" />
            </div>
            <span className="bot-name">AutocassesBot</span>
          </div>
        )}
        
        <div className={`message-bubble ${isUser ? 'user-bubble' : 'bot-bubble'} ${!isFirstInGroup ? 'continuous' : ''}`}>
          {/* Images jointes */}
          {item.images && item.images.length > 0 && (
            <div className="images-container">
              {item.images.map((image, imgIndex) => (
                <div key={imgIndex} className="image-wrapper">
                  <img src={image.url} alt="Message attachment" className="message-image" />
                </div>
              ))}
            </div>
          )}
          
          {/* Texte du message */}
          {item.text && (
            <p className={`message-text ${isUser ? 'user-text' : 'bot-text'}`}>
              {item.text}
            </p>
          )}
          
          {/* Timestamp */}
          <div className="timestamp-container">
            <span className={`timestamp ${isUser ? 'user-timestamp' : 'bot-timestamp'}`}>
              {formatTime(item.timestamp)}
            </span>
            {isUser && (
              <Icon 
                name="CheckCircle" 
                size={12} 
                color={item.read ? "#3b82f6" : "#9ca3af"} 
                className="read-icon"
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  const TypingIndicator = () => (
    <div className="typing-container">
      <div className="bot-avatar">
        <div className="bot-avatar-icon typing">
          <Icon name="Car" size={20} color="#fff" />
        </div>
      </div>
      <div className="typing-bubble">
        <div className="typing-indicator">
          <span className="typing-dot"></span>
          <span className="typing-dot"></span>
          <span className="typing-dot"></span>
        </div>
      </div>
    </div>
  );

  const ImagePickerModal = () => (
    <div className={`modal-overlay ${modalVisible ? 'visible' : ''}`}>
      <div className="modal-content">
        <div className="modal-header">
          <h3 className="modal-title">Ajouter une photo</h3>
          <button 
            className="modal-close"
            onClick={() => setModalVisible(false)}
          >
            <Icon name="X" size={24} color="#6b7280" />
          </button>
        </div>
        
        <div className="modal-options">
          <button 
            className="modal-option"
            onClick={() => {
              setModalVisible(false);
              triggerFileInput();
            }}
          >
            <div className="option-icon" style={{ backgroundColor: '#10b981' }}>
              <Icon name="Image" size={28} color="#fff" />
            </div>
            <span className="option-text">Choisir depuis la galerie</span>
          </button>
        </div>
        
        <p className="modal-hint">
          Maximum 3 images • Taille max: 10MB chaque
        </p>
      </div>
    </div>
  );

  return (
    <div className="bot-container">
      {/* Header */}
      <header className="bot-header">
        <button 
          className="back-button"
          onClick={() => window.history.back()}
        >
          <Icon name="ArrowLeft" size={28} color="#374151" />
        </button>
        
        <div className="header-center">
          <div className="header-avatar">
            <Icon name="MessageCircle" size={24} color="#fff" />
          </div>
          <div>
            <h1 className="header-title">AutocassesBot Assistant</h1>
            <span className="header-subtitle">
              <Icon name="Wifi" size={12} color="#10b981" /> En ligne
            </span>
          </div>
        </div>
        
        <button 
          className="header-button"
          onClick={handleClearChat}
        >
          <Icon name="Trash2" size={22} color="#ef4444" />
        </button>
      </header>

      {/* Suggestions rapides */}
      {showSuggestions && messages.length <= 2 && (
        <div className="suggestions-container">
          <div className="suggestions-header">
            <Icon name="Zap" size={16} color="#f59e0b" />
            <span className="suggestions-title">Suggestions rapides</span>
          </div>
          
          <div className="suggestions-list">
            {quickSuggestions.map((category) => (
              <div key={category.category} className="category-container">
                <div className="category-header">
                  <div className="category-icon" style={{ backgroundColor: category.color + '20' }}>
                    <Icon name={category.icon} size={16} color={category.color} />
                  </div>
                  <span className="category-title">{category.category}</span>
                </div>
                <div className="suggestions-grid">
                  {category.items.map((suggestion) => (
                    <button
                      key={suggestion.id}
                      className="suggestion-item"
                      style={{ borderLeftColor: category.color }}
                      onClick={() => handleQuickSuggestion(suggestion)}
                    >
                      <span className="suggestion-icon">{suggestion.icon}</span>
                      <span className="suggestion-text">
                        {suggestion.text}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="chat-container">
        {/* Zone des messages */}
        <div className="messages-list">
          <div className="welcome-header">
            <h2 className="welcome-title">💬 Bienvenue dans le chat</h2>
            <p className="welcome-subtitle">
              Posez vos questions sur les pièces auto, le diagnostic, les achats...
            </p>
          </div>
          
          {messages.map((message, index) => renderMessage(message, index))}
          
          {isTyping && <TypingIndicator />}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Images sélectionnées */}
        {selectedImages.length > 0 && (
          <div className="selected-images-container">
            <div className="selected-images-header">
              <span className="selected-images-title">
                Images à envoyer ({selectedImages.length}/3)
              </span>
              <button 
                className="clear-all-button"
                onClick={handleClearImages}
              >
                Tout effacer
              </button>
            </div>
            <div className="selected-images-scroll">
              {selectedImages.map((image, index) => (
                <div key={index} className="selected-image-wrapper">
                  <img src={image.url} alt="Selected" className="selected-image" />
                  <button
                    className="remove-image-button"
                    onClick={() => handleRemoveImage(index)}
                  >
                    <div className="remove-image-icon">
                      <Icon name="X" size={14} color="#fff" />
                    </div>
                  </button>
                  <span className="image-file-name" title={image.name}>
                    {image.name.length > 15 ? `${image.name.substring(0, 15)}...` : image.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Input file caché */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleImageSelect}
          style={{ display: 'none' }}
        />

        {/* Zone de saisie */}
        <div className="input-container">
          <button
            className="attachment-button"
            onClick={triggerFileInput}
            title="Ajouter une image"
          >
            <Icon name="Paperclip" size={24} color="#6b7280" />
            {selectedImages.length > 0 && (
              <div className="image-count-badge">
                <span className="image-count-text">{selectedImages.length}</span>
              </div>
            )}
          </button>
          
          <div className="text-input-wrapper">
            <textarea
              ref={inputRef}
              className="text-input"
              placeholder="Écrivez votre message..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyPress}
              rows={1}
              maxLength={1000}
            />
            <span className="char-count">
              {inputText.length}/1000
            </span>
          </div>
          
          <button
            className={`send-button ${(!inputText.trim() && selectedImages.length === 0) ? 'disabled' : ''}`}
            onClick={handleSend}
            disabled={(!inputText.trim() && selectedImages.length === 0) || isLoading}
          >
            {isLoading ? (
              <div className="loading-spinner"></div>
            ) : (
              <Icon 
                name="Send" 
                size={20} 
                color={(!inputText.trim() && selectedImages.length === 0) ? "#9ca3af" : "#fff"} 
              />
            )}
          </button>
        </div>
      </div>

      <ImagePickerModal />
    </div>
  );
};

export default Bot;